import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, ScrollView, Image, Alert, TouchableOpacity, Platform } from 'react-native';
import { 
  Card, 
  Text, 
  Button, 
  List, 
  Chip, 
  FAB, 
  Portal, 
  Modal,
  Divider
} from 'react-native-paper';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSelector, useDispatch } from 'react-redux';
import { ResultsStackParamList } from '../../navigation/AppNavigator';
import { RootState, AppDispatch } from '../../store';
import { colors, spacing, typography, shadows } from '../../constants/theme';
import { ResponseStatus, ReviewStatus } from '../../types';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { fetchReviewById, updateReviewDetails } from '../../store/slices/reviewSlice';
import { selectOfflineSyncSummary } from '../../store/slices/offlineSlice';
import { api } from '../../services/api';
import { buildReviewReportHtml } from '../../services/reportBuilder';
import { dedupeResponsesByItemId, selectBestResponseForItem } from '../../services/reviewResponseSelector';
import { isDetoxTestControlsEnabled, shouldMockNativeSharing } from '../../config/environments';
import { reviewService } from '../../services/reviewService';
import type { ReviewContactDetails } from '../../types';
import { ReviewDetailsModal } from '../../components/ReviewDetailsModal';

// Platform-specific imports
let FileSystem: any;
let Sharing: any;
let Print: any;

if (Platform.OS !== 'web') {
  // Mobile PDF generation imports
  FileSystem = require('expo-file-system/legacy');
  Sharing = require('expo-sharing');
  Print = require('expo-print');
}

// Defensive fallbacks
const ResponseStatusValues = ResponseStatus || {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
};

const ReviewStatusValues = ReviewStatus || {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SUBMITTED: 'submitted',
};

const templateHasItems = (template: any): boolean => {
  const categories = template?.categories;
  if (!Array.isArray(categories) || categories.length === 0) {
    return false;
  }

  const hasItems = (nodes: any[]): boolean =>
    nodes.some(category =>
      (Array.isArray(category?.items) && category.items.length > 0) ||
      (Array.isArray(category?.subcategories) && hasItems(category.subcategories))
    );

  return hasItems(categories);
};

type RouteProps = RouteProp<ResultsStackParamList, 'ResultDetail'>;
type NavigationProps = StackNavigationProp<ResultsStackParamList, 'ResultDetail'>;

const ResultDetailScreen: React.FC = () => {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProps>();
  const dispatch = useDispatch<AppDispatch>();
  
  const { reviewId } = route.params;
  const { isLoadingReview } = useSelector((state: RootState) => state.review);
  const isOnline = useSelector((state: RootState) => selectOfflineSyncSummary(state).isOnline);
  const storedReview = useSelector((state: RootState) => 
    state.review.reviews.find(r => r.id === Number(reviewId))
  );
  const templates = useSelector((state: RootState) => state.review.templates);
  const [detailTemplate, setDetailTemplate] = useState<any>(null);
  const review = useMemo(() => {
    if (!storedReview || templateHasItems(storedReview.template)) {
      return storedReview;
    }

    const templateId = storedReview.templateId || storedReview.template?.id;
    const fallbackTemplate = templateHasItems(detailTemplate) && Number(detailTemplate.id) === Number(templateId)
      ? detailTemplate
      : templates.find(template => Number(template.id) === Number(templateId));

    if (!templateHasItems(fallbackTemplate)) {
      return storedReview;
    }

    const reviewWithTemplate = {
      ...storedReview,
      template: fallbackTemplate,
    };

    if ((storedReview as any).reviewData) {
      (reviewWithTemplate as any).reviewData = {
        ...(storedReview as any).reviewData,
        template: fallbackTemplate,
      };
    }

    return reviewWithTemplate;
  }, [storedReview, templates, detailTemplate]);
  const authToken = useSelector((state: RootState) => state.auth.token);
  
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedResponse, setSelectedResponse] = useState<any>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasLoadAttemptFinished, setHasLoadAttemptFinished] = useState(Boolean(review));
  const [showReviewDetailsModal, setShowReviewDetailsModal] = useState(false);
  const [isSavingReviewDetails, setIsSavingReviewDetails] = useState(false);
  const [reportDeliveryStatus, setReportDeliveryStatus] = useState<string | null>(null);
  const pdfExportCancelledRef = useRef(false);

  // Set up navigation header with back button
  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <TouchableOpacity
          onPress={() => navigation.navigate('ResultsList')}
          style={{ marginLeft: Platform.OS === 'web' ? 16 : 10 }}
        >
          <MaterialCommunityIcons
            name="arrow-left"
            size={24}
            color={Platform.OS === 'web' ? colors.primary : '#fff'}
          />
        </TouchableOpacity>
      ),
    });
  }, [navigation]);

  // Helper function to get photo URL
  const getPhotoUrl = (photo: any): string => {
    // Use presigned URL from photo data if available, otherwise fallback to s3Url or uri
    const url = photo.presignedUrl || photo.s3Url || photo.uri;
    return url;
  };

  // Helper function to format response values
  const formatResponseValue = (value: any, field: any): string => {
    // Skip helpful_links (informational only, no response value)
    if (field?.type === 'helpful_links') {
      return '';
    }

    // Format dimension values
    if (field?.type === 'dimensions_2d' && typeof value === 'object' && value !== null) {
      const unit = field.unit || 'in';
      const length = value.length || 0;
      const width = value.width || 0;
      return `${length} x ${width} ${unit}`;
    }

    if (field?.type === 'dimensions_3d' && typeof value === 'object' && value !== null) {
      const unit = field.unit || 'in';
      const length = value.length || 0;
      const width = value.width || 0;
      const depth = value.depth || 0;
      return `${length} x ${width} x ${depth} ${unit}`;
    }

    // Format boolean values
    if (value === true || value === 'true' || value === 'yes') {
      return 'Yes';
    }
    if (value === false || value === 'false' || value === 'no') {
      return 'No';
    }

    // Default to string conversion
    return String(value);
  };

  const reviewHasTemplate = templateHasItems(review?.template);
  const reviewTemplateHydratedRef = useRef(reviewHasTemplate);
  useEffect(() => {
    reviewTemplateHydratedRef.current = reviewHasTemplate;
  }, [reviewHasTemplate]);

  useEffect(() => {
    const loadReviewData = async () => {
      const numericReviewId = Number(reviewId);
      const shouldIncludeTemplate = !reviewTemplateHydratedRef.current;

      setHasLoadAttemptFinished(false);

      if (Platform.OS !== 'web' || isOnline) {
        await dispatch(fetchReviewById({ reviewId: numericReviewId, includeTemplate: shouldIncludeTemplate }));
      }

      setHasLoadAttemptFinished(true);
      setHasInitialized(true);
    };

    loadReviewData();
  }, [reviewId, dispatch, isOnline]);

  useEffect(() => {
    if (review) {
      setHasLoadAttemptFinished(true);
    }
  }, [review]);

  useEffect(() => {
    const templateId = review?.templateId || review?.template?.id;

    if (!review || reviewHasTemplate || !templateId) {
      return;
    }

    let isCancelled = false;

    const loadTemplateFallback = async () => {
      try {
        const cachedTemplate = await reviewService.getTemplateDetails(Number(templateId));
        if (isCancelled) {
          return;
        }

        if (templateHasItems(cachedTemplate)) {
          setDetailTemplate(cachedTemplate);
          return;
        }

        if (!isOnline) {
          return;
        }

        const freshTemplate = await reviewService.getTemplateDetails(Number(templateId), { preferFresh: true });
        if (!isCancelled && templateHasItems(freshTemplate)) {
          setDetailTemplate(freshTemplate);
        }
      } catch (error) {
        console.warn('[ResultDetailScreen] Failed to hydrate template fallback:', {
          reviewId,
          templateId,
          error,
        });
      }
    };

    loadTemplateFallback();

    return () => {
      isCancelled = true;
    };
  }, [review, reviewHasTemplate, reviewId, isOnline]);

  useEffect(() => {
    pdfExportCancelledRef.current = false;
    return () => {
      pdfExportCancelledRef.current = true;
    };
  }, []);

  // Add focus listener to refresh data when returning from edit screen
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      // Only refresh if we've already initialized (prevents double loading on first load)
      if (hasInitialized) {
        const shouldIncludeTemplate = !reviewTemplateHydratedRef.current;

        if (Platform.OS !== 'web' || isOnline) {
          dispatch(fetchReviewById({ reviewId: Number(reviewId), includeTemplate: shouldIncludeTemplate }));
        }
      }
    });

    return unsubscribe;
  }, [navigation, reviewId, dispatch, hasInitialized, isOnline]);

  // No need to preload photo URLs since they're included in photo data

  useEffect(() => {
    // Only navigate back if we've tried to load and there's no review
    if (!review && hasInitialized && hasLoadAttemptFinished && !isLoadingReview) {
      navigation.goBack();
    }
  }, [review, navigation, hasInitialized, hasLoadAttemptFinished, isLoadingReview]);

  // Show loading state if we're loading OR if we haven't initialized yet
  if (isLoadingReview || (!review && !hasLoadAttemptFinished)) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading Review...</Text>
        </View>
      </View>
    );
  }

  if (!review) return null;

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getResponseByItemId = (itemId: number) => {
    return selectBestResponseForItem(review.responses, itemId);
  };

  const getMultiItemCount = (responseData: any) => {
    if (!responseData) return 1;
    
    // Count unique item numbers from fields with pattern "fieldName_item_N"
    const itemNumbers = new Set();
    Object.keys(responseData).forEach(key => {
      const match = key.match(/_item_(\d+)$/);
      if (match) {
        itemNumbers.add(parseInt(match[1]));
      }
    });
    
    return itemNumbers.size > 0 ? itemNumbers.size : 1;
  };

  // Helper function to render categories with proper hierarchy
  const renderCategoryWithHierarchy = (category: any, level: number = 0) => {
    const categoryItems = category.items || [];
    const completedInCategory = categoryItems.filter((item: any) => 
      getResponseByItemId(item.id)?.status === ResponseStatusValues.COMPLETED
    ).length;
    const isExpanded = expandedCategories.includes(category.id);

    // Recursively count items in subcategories for the stats
    let totalItems = categoryItems.length;
    let totalCompleted = completedInCategory;
    
    if (category.subcategories) {
      category.subcategories.forEach((subcat: any) => {
        const subcatItems = subcat.items || [];
        totalItems += subcatItems.length;
        totalCompleted += subcatItems.filter((item: any) => 
          getResponseByItemId(item.id)?.status === ResponseStatusValues.COMPLETED
        ).length;
      });
    }

    return (
      <Card key={category.id} style={[styles.categoryCard, level > 0 && styles.subcategoryCard]}>
        <List.Accordion
          testID={`result-category-${category.id}`}
          title={category.name}
          description={`${totalCompleted}/${totalItems} completed`}
          expanded={isExpanded}
          onPress={() => toggleCategory(category.id)}
          left={props => (
            <List.Icon
              {...props}
              icon={category.icon || 'folder'}
              color={
                totalCompleted === totalItems
                  ? colors.success
                  : totalCompleted > 0
                  ? colors.warning
                  : colors.error
              }
            />
          )}
        >
          {/* Render items in this category */}
          {categoryItems.map((item: any, itemIndex: number) => {
            const response = getResponseByItemId(item.id);
            const status = response?.status || ResponseStatusValues.NOT_STARTED;
            
            return (
              <View key={item.id} testID={`result-item-${item.id}`} style={[styles.itemContainer, { marginLeft: 24 + (level * 16) }]}>
                {/* Existing item rendering code */}
                <View style={styles.itemHeader}>
                  <View style={styles.itemInfo}>
                    <MaterialCommunityIcons
                      name={getStatusIcon(status)}
                      size={20}
                      color={getStatusColor(status)}
                      style={styles.itemIcon}
                    />
                    <View style={styles.itemDetails}>
                      <Text style={styles.itemName}>
                        {item.name}
                        {response && response.responseData && getMultiItemCount(response.responseData) > 1 && 
                          ` (${getMultiItemCount(response.responseData)})`
                        }
                      </Text>
                      {item.description && (
                        <Text style={styles.itemDescription}>{item.description}</Text>
                      )}
                    </View>
                  </View>
                  
                  {/* View and Edit Buttons */}
                  <View style={styles.buttonContainer}>
                    <Button
                      testID={`result-view-item-${item.id}`}
                      mode="outlined"
                      icon="eye"
                      onPress={() => {
                        setSelectedItem(item);
                        setSelectedResponse(response);
                        setShowItemModal(true);
                      }}
                      style={[styles.actionButton, { marginRight: 8 }]}
                      compact
                    >
                      View
                    </Button>
                    {status === ResponseStatusValues.COMPLETED && (
                      <Button
                        testID={`result-edit-item-${item.id}`}
                        mode="outlined"
                        icon="pencil"
                        onPress={() => {
                          // Delay navigation to prevent iOS crash during accordion animation
                          setTimeout(() => {
                            navigation.navigate('ItemDetail', { 
                              reviewId, 
                              itemId: item.id, 
                              categoryName: category.name 
                            });
                          }, 100);
                        }}
                        style={styles.actionButton}
                        compact
                      >
                        Edit
                      </Button>
                    )}
                  </View>
                </View>

                {response && status === ResponseStatusValues.COMPLETED && (
                  <View style={styles.responseDetails}>
                    {/* Present/Not Present */}
                    <View style={styles.responseRow}>
                      <Text style={styles.responseLabel}>Status:</Text>
                      <Chip
                        mode="flat"
                        compact
                        style={[
                          styles.presenceChip,
                          { backgroundColor: response.isPresent ? colors.success : colors.error }
                        ]}
                        textStyle={{ color: '#fff', fontSize: 12 }}
                      >
                        {response.isPresent ? 'Present' : 'Not Present'}
                      </Chip>
                    </View>

                    {/* Room Location */}
                    {response.room && (
                      <View style={styles.responseRow}>
                        <Text style={styles.responseLabel}>Location:</Text>
                        <Text style={styles.responseValue}>{response.room.name}</Text>
                      </View>
                    )}

                    {/* Notes */}
                    {response.notes && (
                      <View style={styles.responseRow}>
                        <Text style={styles.responseLabel}>Notes:</Text>
                        <Text style={styles.responseValue}>{response.notes}</Text>
                      </View>
                    )}

                    {/* Photos */}
                    {response.photos && response.photos.length > 0 && (
                      <View style={styles.photosSection}>
                        <Text style={styles.responseLabel}>Photos ({response.photos.length}):</Text>
                        <View style={styles.photosGrid}>
                          {response.photos.map((photo: any, index: number) => (
                            <Button
                              key={photo.id}
                              mode="outlined"
                              onPress={() => {
                                const photoUrl = getPhotoUrl(photo);
                                openPhotoModal(photoUrl);
                              }}
                              style={styles.photoButton}
                              icon="image"
                              compact
                            >
                              Photo {index + 1}
                            </Button>
                          ))}
                        </View>
                      </View>
                    )}
                  </View>
                )}

                {itemIndex < categoryItems.length - 1 && <Divider style={styles.itemDivider} />}
              </View>
            );
          })}

          {/* Render subcategories */}
          {category.subcategories && category.subcategories.map((subcat: any) => 
            renderCategoryWithHierarchy(subcat, level + 1)
          )}
        </List.Accordion>
      </Card>
    );
  };

  const getStatusIcon = (status: ResponseStatus | string) => {
    switch (status) {
      case ResponseStatusValues.COMPLETED:
        return 'check-circle';
      case ResponseStatusValues.IN_PROGRESS:
        return 'progress-clock';
      default:
        return 'circle-outline';
    }
  };

  const getStatusColor = (status: ResponseStatus | string) => {
    switch (status) {
      case ResponseStatusValues.COMPLETED:
        return colors.success;
      case ResponseStatusValues.IN_PROGRESS:
        return colors.warning;
      default:
        return colors.error;
    }
  };

  // Helper function to calculate completion stats for nested categories
  const getCategoryStats = (category: any): { completed: number; total: number } => {
    let completed = 0;
    let total = 0;

    // Count items in this category
    const categoryItems = category.items || [];
    total += categoryItems.length;
    completed += categoryItems.filter((item: any) => 
      getResponseByItemId(item.id)?.status === ResponseStatusValues.COMPLETED
    ).length;

    // Recursively count items in subcategories
    const subcategories = category.subcategories || [];
    subcategories.forEach((subcat: any) => {
      const subcatStats = getCategoryStats(subcat);
      completed += subcatStats.completed;
      total += subcatStats.total;
    });

    return { completed, total };
  };

  const getOverallStats = () => {
    // Handle case where review data might not include full template
    if (!review.responses || review.responses.length === 0) {
      return { total: 0, completed: 0, inProgress: 0, notStarted: 0, percentage: 0 };
    }
    
    // Use responses to calculate stats if template isn't loaded
    const responses = dedupeResponsesByItemId(review.responses);
    const totalResponses = responses.length;
    const completed = responses.filter(r => r.status === ResponseStatusValues.COMPLETED).length;
    const inProgress = responses.filter(r => r.status === ResponseStatusValues.IN_PROGRESS).length;
    const notStarted = responses.filter(r => r.status === ResponseStatusValues.NOT_STARTED).length;
    
    return {
      total: totalResponses,
      completed,
      inProgress,
      notStarted,
      percentage: totalResponses > 0 ? Math.round((completed / totalResponses) * 100) : 0
    };
  };

  const stats = getOverallStats();
  const submittedDate = review.submittedAt ? new Date(review.submittedAt).toLocaleDateString() : null;
  const completedDate = review.completedAt ? new Date(review.completedAt).toLocaleDateString() : null;
  const resultTemplateDebugState = (() => {
    const templateId = review.templateId || review.template?.id || 'missing';
    const categories = Array.isArray(review.template?.categories) ? review.template.categories : [];
    const itemCount = categories.reduce((count: number, category: any) => {
      const countItems = (nodes: any[]): number => nodes.reduce((total, node) => {
        const directItems = Array.isArray(node?.items) ? node.items.length : 0;
        const childItems = Array.isArray(node?.subcategories) ? countItems(node.subcategories) : 0;
        return total + directItems + childItems;
      }, 0);

      return count + countItems([category]);
    }, 0);

    return reviewHasTemplate
      ? 'ready'
      : `pending:template=${templateId}:categories=${categories.length}:items=${itemCount}`;
  })();
  

  const openPhotoModal = (photoUrl: string) => {
    setSelectedPhoto(photoUrl);
    setShowPhotoModal(true);
  };

  const exportResultsAsPDF = async () => {
    try {
      pdfExportCancelledRef.current = false;
      setIsExportingPDF(true);

      if (isDetoxTestControlsEnabled || shouldMockNativeSharing) {
        setShareStatus('PDF share prepared');
        Alert.alert('Share Prepared', 'PDF report is ready to share.');
        setIsExportingPDF(false);
        return;
      }

      const startedJob = await api.startReviewPDFExportJob(Number(reviewId));
      const startedAt = Date.now();
      let completedJob = await api.getReviewPDFExportJob(Number(reviewId), startedJob.jobId);

      while (completedJob.status !== 'completed') {
        if (pdfExportCancelledRef.current) {
          return;
        }

        if (completedJob.status === 'failed') {
          throw new Error(completedJob.errorMessage || 'PDF export failed. Please try again.');
        }

        if (Date.now() - startedAt > 15 * 60 * 1000) {
          throw new Error('PDF export is still processing. Please try again in a few minutes.');
        }

        const delayMs = Date.now() - startedAt < 30 * 1000 ? 2000 : 5000;
        await new Promise(resolve => setTimeout(resolve, delayMs));
        completedJob = await api.getReviewPDFExportJob(Number(reviewId), startedJob.jobId);
      }

      if (pdfExportCancelledRef.current) {
        return;
      }

      if (!completedJob.downloadUrl) {
        throw new Error('PDF export completed but no download URL was returned.');
      }

      if (Platform.OS === 'web') {
        const a = document.createElement('a');
        a.href = completedJob.downloadUrl;
        const addressForFilename = review.property?.address?.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '') || 'report';
        a.download = `review-${addressForFilename}-${new Date().toISOString().split('T')[0]}.pdf`;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        // For mobile, save PDF to file system and share
        if (FileSystem && Sharing) {
          try {
            const addressForFilename = review.property?.address?.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '') || 'report';
            const filename = `review-${addressForFilename}-${new Date().toISOString().split('T')[0]}.pdf`;
            const targetDirectory = FileSystem.cacheDirectory || FileSystem.documentDirectory;
            if (!targetDirectory) {
              throw new Error('No writable file-system directory is available for PDF export.');
            }

            const fileUri = targetDirectory + filename;
            const downloadResult = await FileSystem.downloadAsync(completedJob.downloadUrl, fileUri);
            if (
              typeof downloadResult.status === 'number' &&
              (downloadResult.status < 200 || downloadResult.status >= 300)
            ) {
              throw new Error(`PDF download failed with status ${downloadResult.status}.`);
            }

            const isAvailable = await Sharing.isAvailableAsync();
            if (isAvailable) {
              await Sharing.shareAsync(downloadResult.uri, {
                mimeType: 'application/pdf',
                dialogTitle: 'Share PDF Report',
                UTI: 'com.adobe.pdf'
              });
            } else {
              Alert.alert('Export Complete', `PDF saved to ${filename}`);
            }
          } catch (error) {
            console.warn('[ResultDetailScreen] Error saving/sharing PDF on mobile:', {
              message: error instanceof Error ? error.message : String(error),
              hasFileSystem: Boolean(FileSystem),
              hasDownloadAsync: typeof FileSystem?.downloadAsync === 'function',
              hasWritableDirectory: Boolean(FileSystem?.cacheDirectory || FileSystem?.documentDirectory),
              hasSharing: Boolean(Sharing),
            });
            Alert.alert('Export Failed', 'Failed to save PDF on device.');
          }
        } else {
          Alert.alert('Export Complete', 'PDF has been generated but file system access is not available.');
        }
      }
      
      if (!pdfExportCancelledRef.current) {
        setIsExportingPDF(false);
      }
    } catch (error) {
      console.error('PDF Export error:', error);

      if (pdfExportCancelledRef.current) {
        return;
      }

      setIsExportingPDF(false);
      
      let errorMessage = 'Failed to export PDF. Please try again.';
      if (error instanceof Error) {
        if (error.message.includes('not found')) {
          errorMessage = 'This review could not be found. It may have been deleted or you may not have access to it.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      }
      
      Alert.alert('Export Failed', errorMessage);
    }
  };

  const exportResults = async () => {
    try {
      const html = buildReviewReportHtml(review, {
        authToken,
        apiBaseUrl: process.env.EXPO_PUBLIC_API_URL,
      });
      
      if (Platform.OS === 'web') {
        // For web, create a blob and download HTML
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Replace spaces with dashes for filename
        const addressForFilename = review.property?.address?.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '') || 'report';
        a.download = `review-${addressForFilename}-${new Date().toISOString().split('T')[0]}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        Alert.alert('Export Complete', 'The review report has been downloaded as an HTML file.');
      } else {
        // For mobile, use expo-print
        const { uri } = await Print.printToFileAsync({ 
          html,
          base64: false
        });
        
        // Create a proper filename
        const addressForFilename = review.property?.address?.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '') || 'report';
        const filename = `review-${addressForFilename}-${new Date().toISOString().split('T')[0]}.pdf`;
        
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(uri, {
            mimeType: 'application/pdf',
            dialogTitle: 'Share Review Report',
            UTI: 'com.adobe.pdf',
            // Note: Expo Sharing doesn't support custom filenames directly
            // The file will be shared with system-generated name, but receiving apps may use our title
          });
        } else {
          Alert.alert('Export Complete', 'The PDF has been saved to your device.');
        }
      }
    } catch (error) {
      console.error('Export error:', error);
      Alert.alert('Export Failed', 'Failed to export review data. Please try again.');
    }
  };

  const saveReviewDetails = async (details: ReviewContactDetails) => {
    setIsSavingReviewDetails(true);
    try {
      await dispatch(updateReviewDetails({ reviewId: Number(reviewId), details })).unwrap();
      setShowReviewDetailsModal(false);
    } catch (saveError) {
      Alert.alert('Error', 'Failed to save review details. Please try again.');
      throw saveError;
    } finally {
      setIsSavingReviewDetails(false);
    }
  };

  const saveAndRequestResend = async (details: ReviewContactDetails) => {
    await saveReviewDetails(details);
    try {
      const resend = await reviewService.resendReviewReportWithDetails(
        Number(reviewId),
        details
      );
      if (!resend.queued) {
        setReportDeliveryStatus('Saved offline — not queued');
        Alert.alert(
          'Details saved offline',
          'Reconnect and choose “Save and resend report” again. No report was queued while offline.'
        );
        return;
      }

      setReportDeliveryStatus('Queued');
      Alert.alert(
        'Report queued',
        resend.result.message || 'The report will be resent to its recipients.'
      );
    } catch (resendError) {
      Alert.alert('Resend failed', 'Details were saved, but the report could not be queued for resend.');
      throw resendError;
    }
  };

  const openReviewDetails = async () => {
    setShowReviewDetailsModal(true);
    try {
      const deliveries = await reviewService.getReviewReportDeliveries(Number(reviewId));
      setReportDeliveryStatus(
        deliveries.length > 0
          ? deliveries.map(delivery => delivery.status).join(', ')
          : 'Not sent'
      );
    } catch {
      setReportDeliveryStatus('Unavailable');
    }
  };

  return (
    <View testID="result-detail-screen" style={styles.container}>
      <Text testID="result-template-ready" style={styles.detoxStateMarker}>
        {resultTemplateDebugState}
      </Text>
      <ScrollView 
        testID="result-detail-scroll"
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header Summary */}
        <Card style={styles.headerCard}>
          <Card.Content>
            <Text style={styles.propertyAddress}>
              {review.property?.address || 'Unknown Address'}
            </Text>
            <View style={styles.memberDetailsRow}>
              <View style={styles.memberDetailsText}>
                <Text testID="result-detail-member-name" style={styles.memberName}>
                  {review.memberName || 'Member name not set'}
                </Text>
                {review.homeManagerName ? (
                  <Text style={styles.memberSecondary}>Home manager: {review.homeManagerName}</Text>
                ) : null}
              </View>
              <Button
                testID="result-edit-review-details-button"
                mode="outlined"
                icon="account-edit-outline"
                compact
                onPress={openReviewDetails}
              >
                Edit details
              </Button>
            </View>
            <Text style={styles.companyName}>
              {review.template?.name || 'Standard Review'}
            </Text>
            
            <View style={styles.statusContainer}>
              <Chip
                mode="flat"
                style={[
                  styles.statusChip, 
                  { backgroundColor: review.status === ReviewStatusValues.SUBMITTED ? colors.primary : colors.success }
                ]}
                textStyle={{ color: '#fff' }}
                icon={review.status === ReviewStatusValues.SUBMITTED ? 'check-all' : 'check-circle'}
              >
                {review.status === ReviewStatusValues.SUBMITTED ? 'Submitted' : 'Completed'}
              </Chip>
              
              {(submittedDate || completedDate) && (
                <Text style={styles.dateText}>
                  {submittedDate ? `Submitted: ${submittedDate}` : `Completed: ${completedDate}`}
                </Text>
              )}
            </View>

            {/* Overall Statistics */}
            <View style={styles.statsSection}>
              <Text style={styles.statsTitle}>Overall Progress</Text>
              <View style={styles.statsGrid}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{stats.completed}</Text>
                  <Text style={styles.statLabel}>Completed</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{stats.inProgress}</Text>
                  <Text style={styles.statLabel}>In Progress</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{stats.notStarted}</Text>
                  <Text style={styles.statLabel}>Not Started</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: colors.primary }]}>{stats.percentage}%</Text>
                  <Text style={styles.statLabel}>Complete</Text>
                </View>
              </View>
            </View>
          </Card.Content>
        </Card>

        {/* Edit Info Banner */}
        <Card style={styles.editInfoCard}>
          <Card.Content>
            <View style={styles.editInfoHeader}>
              <MaterialCommunityIcons name="information" size={20} color={colors.primary} />
              <Text style={styles.editInfoTitle}>Editable Review</Text>
            </View>
            <Text style={styles.editInfoText}>
              Individual items can be edited even after submission. Look for the "Edit" button next to completed items to make changes.
            </Text>
          </Card.Content>
        </Card>

        {/* General Notes */}
        {review.notes && (
          <Card style={styles.notesCard}>
            <Card.Title title="General Notes" />
            <Card.Content>
              <Text style={styles.notesText}>{review.notes}</Text>
            </Card.Content>
          </Card>
        )}

        {/* Vendor Notes */}
        {review.vendorNotes && (
          <Card style={styles.card}>
            <Card.Content>
              <Text style={styles.sectionTitle}>Notes for Vendors</Text>
              <Text style={styles.vendorNotesText}>{review.vendorNotes}</Text>
            </Card.Content>
          </Card>
        )}

        {/* Categories and Items */}
        {!review.template?.categories || review.template.categories.length === 0 || 
         !review.template.categories.some(cat => cat.items && cat.items.length > 0) ? (
          <Card style={styles.card}>
            <Card.Content>
              <Text style={styles.noDataText}>
                Detailed review data is not available. The review has {stats.total} responses 
                with {stats.completed} completed.
              </Text>
            </Card.Content>
          </Card>
        ) : (
          review.template.categories
            .filter(category => !category.parentCategoryId) // Only show top-level categories
            .map(category => renderCategoryWithHierarchy(category, 0))
        )}

        {/* On-Demand Items Section */}
        {review.onDemandItems && review.onDemandItems.length > 0 && (
          <Card style={styles.categoryCard}>
            <List.Accordion
              testID="result-category-custom-items"
              title="Custom Items"
              description={`${review.onDemandItems.filter((item: any) => 
                getResponseByItemId(item.id)?.status === ResponseStatusValues.COMPLETED
              ).length}/${review.onDemandItems.length} completed`}
              expanded={expandedCategories.includes(-1)}
              onPress={() => toggleCategory(-1)}
              left={props => {
                const completedCount = review.onDemandItems.filter((item: any) => 
                  getResponseByItemId(item.id)?.status === ResponseStatusValues.COMPLETED
                ).length;
                const allCompleted = completedCount === review.onDemandItems.length;
                
                return (
                  <List.Icon
                    {...props}
                    icon={allCompleted ? 'check-circle' : 'plus-box'}
                    color={allCompleted ? colors.success : colors.secondary}
                  />
                );
              }}
            >
              {review.onDemandItems.map((item: any) => {
                const response = getResponseByItemId(item.id);
                const status = response?.status || ResponseStatusValues.NOT_STARTED;
                
                return (
                  <View key={item.id} testID={`result-item-${item.id}`} style={[styles.itemContainer, { marginLeft: 24 }]}>
                    <View style={styles.itemHeader}>
                      <View style={styles.itemInfo}>
                        <MaterialCommunityIcons
                          name={getStatusIcon(status)}
                          size={20}
                          color={getStatusColor(status)}
                          style={styles.itemIcon}
                        />
                        <View style={styles.itemDetails}>
                          <Text style={styles.itemName}>
                            {item.name}
                            {response && response.responseData && getMultiItemCount(response.responseData) > 1 && 
                              ` (${getMultiItemCount(response.responseData)})`
                            }
                          </Text>
                          {item.description && (
                            <Text style={styles.itemDescription}>{item.description}</Text>
                          )}
                        </View>
                      </View>
                      
                      {/* View and Edit Buttons */}
                      <View style={styles.buttonContainer}>
                        <Button
                          testID={`result-view-item-${item.id}`}
                          mode="outlined"
                          icon="eye"
                          onPress={() => {
                            setSelectedItem(item);
                            setSelectedResponse(response);
                            setShowItemModal(true);
                          }}
                          style={[styles.actionButton, { marginRight: 8 }]}
                          compact
                        >
                          View
                        </Button>
                        {status === ResponseStatusValues.COMPLETED && (
                          <Button
                            testID={`result-edit-item-${item.id}`}
                            mode="outlined"
                            icon="pencil"
                            onPress={() => {
                              setTimeout(() => {
                                navigation.navigate('ItemDetail', { 
                                  reviewId, 
                                  itemId: item.id, 
                                  categoryName: 'Custom Items' 
                                });
                              }, 100);
                            }}
                            style={styles.actionButton}
                            compact
                          >
                            Edit
                          </Button>
                        )}
                      </View>
                    </View>

                    {/* Show response details if completed */}
                    {response && status === ResponseStatusValues.COMPLETED && (
                      <View style={styles.responseDetails}>
                        {/* Present/Not Present */}
                        <View style={styles.responseRow}>
                          <Text style={styles.responseLabel}>Status:</Text>
                          <Chip
                            mode="flat"
                            compact
                            style={[
                              styles.presenceChip,
                              { backgroundColor: response.isPresent ? colors.success : colors.error }
                            ]}
                            textStyle={{ color: '#fff', fontSize: 12 }}
                          >
                            {response.isPresent ? 'Present' : 'Not Present'}
                          </Chip>
                        </View>

                        {/* Room Location */}
                        {response.room && (
                          <View style={styles.responseRow}>
                            <Text style={styles.responseLabel}>Location:</Text>
                            <Text style={styles.responseValue}>{response.room.name}</Text>
                          </View>
                        )}

                        {/* Notes */}
                        {response.notes && (
                          <View style={styles.responseRow}>
                            <Text style={styles.responseLabel}>Notes:</Text>
                            <Text style={styles.responseValue}>{response.notes}</Text>
                          </View>
                        )}

                        {/* Additional response data */}
                        {response.responseData && Object.entries(response.responseData).map(([key, value]) => {
                          if (value !== null && value !== undefined && value !== '' && key !== 'isPresent') {
                            // Find the field definition to get type and unit info
                            const item = review?.template?.categories?.flatMap((c: any) => c.items || [])
                              .find((i: any) => response.itemId === i.id);
                            const field = item?.questionsSchema?.fields?.find((f: any) => f.id === key);
                            const displayValue = formatResponseValue(value, field);

                            // Skip empty values (e.g., helpful_links)
                            if (!displayValue) {
                              return null;
                            }

                            // Format field label to be more readable
                            let fieldLabel = field?.label || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

                            // Truncate very long labels and add ellipsis
                            if (fieldLabel.length > 20) {
                              fieldLabel = fieldLabel.substring(0, 17) + '...';
                            }

                            return (
                              <View key={key} style={styles.responseRow}>
                                <Text style={styles.responseLabel}>{fieldLabel}:</Text>
                                <Text style={styles.responseValue}>{displayValue}</Text>
                              </View>
                            );
                          }
                          return null;
                        })}
                      </View>
                    )}

                    {/* Show photos if available */}
                    {response && response.photos && response.photos.length > 0 && (
                      <View style={styles.photosSection}>
                        <Text style={styles.photosLabel}>Photos ({response.photos.length}):</Text>
                        <View style={styles.photosContainer}>
                          {response.photos.map((photo: any, index: number) => (
                            <Button
                              key={photo.id}
                              mode="outlined"
                              onPress={() => {
                                const photoUrl = getPhotoUrl(photo);
                                openPhotoModal(photoUrl);
                              }}
                              style={styles.photoButton}
                              icon="image"
                              compact
                            >
                              Photo {index + 1}
                            </Button>
                          ))}
                        </View>
                      </View>
                    )}
                  </View>
                );
              })}
            </List.Accordion>
          </Card>
        )}
      </ScrollView>

      {shareStatus && (
        <Text testID="result-share-status" style={styles.shareStatus}>
          {shareStatus}
        </Text>
      )}

      {/* Export FAB */}
      <FAB
        testID="result-export-pdf-button"
        icon={isExportingPDF ? "loading" : "download"}
        style={styles.fab}
        onPress={exportResultsAsPDF}
        label={isExportingPDF ? 'Generating PDF...' : (Platform.OS === 'web' ? 'Export PDF' : 'Export')}
        disabled={isExportingPDF}
        loading={isExportingPDF}
      />

      {/* Photo Modal */}
      <Portal>
        <Modal
          visible={showPhotoModal}
          onDismiss={() => setShowPhotoModal(false)}
          contentContainerStyle={styles.photoModal}
        >
          {selectedPhoto && (
            <View style={styles.photoContainer}>
              <Image source={{ uri: selectedPhoto }} style={styles.fullPhoto} />
              <Button
                mode="contained"
                onPress={() => setShowPhotoModal(false)}
                style={styles.closePhotoButton}
                icon="close"
              >
                Close
              </Button>
            </View>
          )}
        </Modal>

        {/* Item Detail Modal */}
        <Modal
          visible={showItemModal}
          onDismiss={() => setShowItemModal(false)}
          contentContainerStyle={styles.itemModal}
        >
          {selectedItem && selectedResponse && (
            <ScrollView 
              testID="result-item-detail-modal"
              style={styles.modalScrollView}
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>{selectedItem.name}</Text>
                {selectedItem.description && (
                  <Text style={styles.modalDescription}>{selectedItem.description}</Text>
                )}
              </View>

              <Divider style={styles.modalDivider} />

              {/* Response Data */}
              <View style={styles.modalSection}>
                <Text style={styles.modalSectionTitle}>Response Details</Text>
                
                {/* Present/Not Present Status */}
                <View style={styles.modalRow}>
                  <Text style={styles.modalLabel}>Status:</Text>
                  <Chip
                    mode="flat"
                    compact
                    style={[
                      styles.presenceChip,
                      { backgroundColor: selectedResponse.isPresent ? colors.success : colors.error }
                    ]}
                    textStyle={{ color: '#fff', fontSize: 12 }}
                  >
                    {selectedResponse.isPresent ? 'Present' : 'Not Present'}
                  </Chip>
                </View>

                {/* Room Location */}
                {selectedResponse.room && (
                  <View style={styles.modalRow}>
                    <Text style={styles.modalLabel}>Location:</Text>
                    <Text style={styles.modalValue}>{selectedResponse.room.name}</Text>
                  </View>
                )}

                {/* Response Data Fields */}
                {selectedResponse.responseData && Object.keys(selectedResponse.responseData).length > 0 && (
                  <>
                    <Text style={styles.modalSubtitle}>Item Details:</Text>
                    {(() => {
                      const responseData = selectedResponse.responseData;
                      const multiItemCount = getMultiItemCount(responseData);
                      
                      if (multiItemCount > 1) {
                        // Group multi-item data by item number
                        const itemGroups: { [itemNumber: number]: Array<{key: string, value: any, field: any}> } = {};
                        const nonItemFields: Array<{key: string, value: any}> = [];
                        
                        Object.entries(responseData).forEach(([key, value]) => {
                          const itemMatch = key.match(/^(.+)_item_(\d+)$/);
                          if (itemMatch) {
                            const [, baseFieldName, itemNumber] = itemMatch;
                            const itemNum = parseInt(itemNumber);
                            const field = selectedItem.questionsSchema?.fields?.find((f: any) => 
                              f.id === baseFieldName || f.label?.toLowerCase().includes(baseFieldName.toLowerCase())
                            );
                            
                            if (!itemGroups[itemNum]) {
                              itemGroups[itemNum] = [];
                            }
                            itemGroups[itemNum].push({ key: baseFieldName, value, field });
                          } else {
                            nonItemFields.push({ key, value });
                          }
                        });
                        
                        return (
                          <>
                            {/* Show non-item fields first (like quantity) */}
                            {nonItemFields.map(({ key, value }) => {
                              // Skip roomId fields - room is shown separately
                              if (key.toLowerCase() === 'roomid' || key.toLowerCase() === 'room') {
                                return null;
                              }
                              
                              const field = selectedItem.questionsSchema?.fields?.find((f: any) => f.id === key);
                              const label = field?.label || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                              const displayValue = formatResponseValue(value, field) || 'Not specified';

                              if (!displayValue) {
                                return null; // Skip empty values (e.g., helpful_links)
                              }

                              return (
                                <View key={key} style={styles.modalRow}>
                                  <Text style={styles.modalLabel}>{label}:</Text>
                                  <Text style={styles.modalValue}>{displayValue}</Text>
                                </View>
                              );
                            })}
                            
                            {/* Show grouped items */}
                            {Object.entries(itemGroups).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([itemNumber, fields]) => (
                              <View key={itemNumber} style={styles.itemGroup}>
                                <Text style={[styles.modalSubtitle, { marginTop: 10, marginBottom: 5 }]}>
                                  {selectedItem.name} {parseInt(itemNumber) + 1}:
                                </Text>
                                {fields.map(({ key, value, field }) => {
                                  // Skip roomId fields - room is shown separately
                                  if (key.toLowerCase() === 'roomid' || key.toLowerCase() === 'room') {
                                    return null;
                                  }
                                  
                                  const label = field?.label || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                  const displayValue = formatResponseValue(value, field) || 'Not specified';

                                  if (!displayValue) {
                                    return null; // Skip empty values (e.g., helpful_links)
                                  }

                                  return (
                                    <View key={`${itemNumber}-${key}`} style={[styles.modalRow, { marginLeft: 15 }]}>
                                      <Text style={styles.modalLabel}>{label}:</Text>
                                      <Text style={styles.modalValue}>{displayValue}</Text>
                                    </View>
                                  );
                                })}
                              </View>
                            ))}
                          </>
                        );
                      } else {
                        // Single item - show fields normally
                        return Object.entries(responseData).map(([key, value]) => {
                          // Skip roomId fields - room is shown separately
                          if (key.toLowerCase() === 'roomid' || key.toLowerCase() === 'room') {
                            return null;
                          }
                          
                          const field = selectedItem.questionsSchema?.fields?.find((f: any) => f.id === key);
                          const label = field?.label || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                          const displayValue = formatResponseValue(value, field) || 'Not specified';

                          if (!displayValue) {
                            return null; // Skip empty values (e.g., helpful_links)
                          }

                          return (
                            <View key={key} style={styles.modalRow}>
                              <Text style={styles.modalLabel}>{label}:</Text>
                              <Text style={styles.modalValue}>{displayValue}</Text>
                            </View>
                          );
                        });
                      }
                    })()}
                  </>
                )}

                {/* Notes */}
                {selectedResponse.notes && (
                  <>
                    <Text style={styles.modalSubtitle}>Notes:</Text>
                    <Text style={styles.modalNotes}>{selectedResponse.notes}</Text>
                  </>
                )}

                {/* Photos */}
                {selectedResponse.photos && selectedResponse.photos.length > 0 && (
                  <>
                    <Text style={styles.modalSubtitle}>Photos:</Text>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.photoScroll}>
                      {selectedResponse.photos.map((photo: any, index: number) => (
                        <TouchableOpacity
                          key={photo.id}
                          onPress={() => {
                            const photoUrl = getPhotoUrl(photo);
                            openPhotoModal(photoUrl);
                            setShowItemModal(false);
                          }}
                          style={styles.thumbnailContainer}
                        >
                          <Image 
                            source={{ 
                              uri: getPhotoUrl(photo) 
                            }} 
                            style={styles.thumbnail}
                            onError={(error) => {
                              // Image failed to load
                            }}
                            onLoad={() => {
                              // Image loaded successfully
                            }}
                          />
                          <Text style={styles.photoLabel}>Photo {index + 1}</Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </>
                )}
              </View>

              <Button
                mode="contained"
                onPress={() => setShowItemModal(false)}
                style={styles.modalCloseButton}
                icon="close"
              >
                Close
              </Button>
            </ScrollView>
          )}
        </Modal>
      </Portal>
      <ReviewDetailsModal
        visible={showReviewDetailsModal}
        initialDetails={{
          memberName: review.memberName || '',
          memberEmail: review.memberEmail,
          homeManagerName: review.homeManagerName,
          homeManagerEmail: review.homeManagerEmail,
        }}
        loading={isSavingReviewDetails}
        showResend
        deliveryStatus={reportDeliveryStatus}
        onDismiss={() => setShowReviewDetailsModal(false)}
        onSave={saveReviewDetails}
        onResendRequested={saveAndRequestResend}
      />
    </View>
  );
};

const styles: any = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    ...typography.h3,
    color: colors.textSecondary,
  },
  detoxStateMarker: {
    height: 1,
    opacity: 0.01,
  },
  scrollContent: {
    paddingBottom: spacing.xxl,
  },
  headerCard: {
    margin: spacing.md,
    ...shadows.md,
  },
  propertyAddress: {
    ...typography.h3,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  memberDetailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  memberDetailsText: {
    flex: 1,
  },
  memberName: {
    ...typography.body1,
    color: colors.text,
    fontWeight: '600',
  },
  memberSecondary: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  companyName: {
    ...typography.body2,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
  },
  statusChip: {
    alignSelf: 'flex-start',
  },
  dateText: {
    ...typography.caption,
    color: colors.textSecondary,
  },
  statsSection: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.gray200,
  },
  statsTitle: {
    ...typography.h4,
    color: colors.text,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statCard: {
    alignItems: 'center',
    flex: 1,
  },
  statNumber: {
    ...typography.h2,
    color: colors.text,
    fontWeight: 'bold',
  },
  statLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  card: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    ...shadows.sm,
  },
  categoryCard: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    ...shadows.sm,
  },
  subcategoryCard: {
    marginLeft: spacing.lg,
    marginTop: spacing.sm,
    backgroundColor: colors.gray50,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
  },
  sectionTitle: {
    ...typography.h4,
    color: colors.text,
    marginBottom: spacing.md,
  },
  vendorNotesText: {
    ...typography.body2,
    color: colors.text,
    lineHeight: 20,
  },
  itemContainer: {
    paddingLeft: spacing.lg,
    paddingVertical: spacing.sm,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  itemInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
    marginRight: spacing.sm,
  },
  itemIcon: {
    marginTop: 2,
    marginRight: spacing.sm,
  },
  itemDetails: {
    flex: 1,
  },
  itemName: {
    ...typography.body1,
    color: colors.text,
    fontWeight: '600',
    marginBottom: spacing.xs,
  },
  itemDescription: {
    ...typography.body2,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  requiredChip: {
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
  },
  requiredText: {
    fontSize: 10,
    color: colors.error,
  },
  responseDetails: {
    marginLeft: spacing.lg,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.gray200,
  },
  responseRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  responseLabel: {
    ...typography.body2,
    color: colors.textSecondary,
    fontWeight: '600',
    minWidth: 80,
    marginRight: 8,
  },
  responseValue: {
    ...typography.body2,
    color: colors.text,
    flex: 1,
  },
  presenceChip: {
    marginLeft: spacing.sm,
  },
  photosSection: {
    marginTop: spacing.sm,
  },
  photosGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  photoButton: {
    marginRight: spacing.sm,
    marginBottom: spacing.sm,
  },
  itemDivider: {
    marginTop: spacing.md,
    marginLeft: spacing.lg,
  },
  fab: {
    position: 'absolute',
    margin: spacing.md,
    right: 0,
    bottom: 0,
    backgroundColor: colors.primary,
  },
  shareStatus: {
    position: 'absolute',
    left: spacing.md,
    bottom: spacing.lg,
    color: colors.success,
    fontWeight: '600',
  },
  photoModal: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
  },
  photoContainer: {
    width: '90%',
    height: '80%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 8,
    padding: spacing.md,
    ...shadows.lg,
  },
  fullPhoto: {
    width: '100%',
    height: '85%',
    resizeMode: 'contain',
  },
  closePhotoButton: {
    marginTop: spacing.lg,
  },
  editButton: {
    alignSelf: 'flex-start',
  },
  notesCard: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    backgroundColor: '#f0f9ff',
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    ...shadows.sm,
  },
  notesText: {
    ...typography.body2,
    color: colors.text,
    lineHeight: 22,
  },
  editInfoCard: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    backgroundColor: '#e3f2fd',
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    ...shadows.sm,
  },
  editInfoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  editInfoTitle: {
    ...typography.body1,
    fontWeight: '600',
    color: colors.primary,
    marginLeft: spacing.xs,
  },
  editInfoText: {
    ...typography.body2,
    color: '#1565c0',
    lineHeight: 18,
  },
  noDataText: {
    ...typography.body1,
    color: colors.gray500,
    textAlign: 'center',
    paddingVertical: spacing.lg,
  },
  buttonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    marginTop: spacing.sm,
  },
  itemModal: {
    backgroundColor: 'white',
    margin: spacing.lg,
    borderRadius: 8,
    maxHeight: '80%',
    ...shadows.lg,
  },
  modalScrollView: {
    maxHeight: '100%',
  },
  modalHeader: {
    padding: spacing.lg,
    paddingBottom: spacing.md,
  },
  modalTitle: {
    ...typography.h3,
    color: colors.primary,
    marginBottom: spacing.xs,
  },
  modalDescription: {
    ...typography.body2,
    color: colors.gray600,
  },
  modalDivider: {
    backgroundColor: colors.gray200,
  },
  modalSection: {
    padding: spacing.lg,
  },
  modalSectionTitle: {
    ...typography.h4,
    color: colors.text,
    marginBottom: spacing.md,
  },
  modalSubtitle: {
    ...typography.body1,
    fontWeight: '600',
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  modalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  modalLabel: {
    ...typography.body2,
    color: colors.gray600,
    marginRight: spacing.sm,
    minWidth: 100,
  },
  modalValue: {
    ...typography.body2,
    color: colors.text,
    flex: 1,
  },
  modalNotes: {
    ...typography.body2,
    color: colors.text,
    marginTop: spacing.xs,
    lineHeight: 20,
  },
  photoScroll: {
    marginTop: spacing.sm,
  },
  thumbnailContainer: {
    marginRight: spacing.md,
    alignItems: 'center',
  },
  thumbnail: {
    width: 100,
    height: 100,
    borderRadius: 8,
    marginBottom: spacing.xs,
  },
  photoLabel: {
    ...typography.caption,
    color: colors.gray600,
  },
  modalCloseButton: {
    margin: spacing.lg,
    marginTop: 0,
  },
  itemGroup: {
    marginVertical: spacing.xs,
    paddingLeft: spacing.sm,
    borderLeftWidth: 2,
    borderLeftColor: colors.primary + '20',
  },
});

export default ResultDetailScreen;
